import { Entity, Vector2, AttackHitbox } from './types'

export class Player implements Entity {
  position: Vector2
  velocity: Vector2
  width = 32
  height = 48
  health = 100
  maxHealth = 100

  private grounded = false
  private jumpCount = 0
  private maxJumps = 2
  private attackCooldown = 0
  private attackDuration = 0
  private facing = 1 // 1 = right, -1 = left
  private animFrame = 0
  private animTimer = 0

  private keys: Set<string> = new Set()

  constructor(x: number, y: number) {
    this.position = { x, y }
    this.velocity = { x: 0, y: 0 }

    window.addEventListener('keydown', (e) =>
      this.keys.add(e.key.toLowerCase()),
    )
    window.addEventListener('keyup', (e) =>
      this.keys.delete(e.key.toLowerCase()),
    )
  }

  update(deltaTime: number, groundY: number): AttackHitbox | null {
    const speed = 5
    const jumpPower = 12
    const gravity = 0.6

    // Horizontal movement
    if (this.keys.has('arrowleft') || this.keys.has('a')) {
      this.velocity.x = -speed
      this.facing = -1
    } else if (this.keys.has('arrowright') || this.keys.has('d')) {
      this.velocity.x = speed
      this.facing = 1
    } else {
      this.velocity.x *= 0.8 // Friction
    }

    // Jumping
    if (
      (this.keys.has(' ') || this.keys.has('arrowup') || this.keys.has('w')) &&
      this.jumpCount < this.maxJumps
    ) {
      if (!this.keys.has('_jumped')) {
        this.velocity.y = -jumpPower
        this.jumpCount++
        this.keys.add('_jumped')
      }
    } else if (
      !this.keys.has(' ') &&
      !this.keys.has('arrowup') &&
      !this.keys.has('w')
    ) {
      this.keys.delete('_jumped')
    }

    // Gravity
    this.velocity.y += gravity

    // Update position
    this.position.x += this.velocity.x
    this.position.y += this.velocity.y

    // Ground collision
    if (this.position.y + this.height >= groundY) {
      this.position.y = groundY - this.height
      this.velocity.y = 0
      this.grounded = true
      this.jumpCount = 0
    } else {
      this.grounded = false
    }

    // Screen bounds
    this.position.x = Math.max(0, Math.min(800 - this.width, this.position.x))

    // Attack
    this.attackCooldown = Math.max(0, this.attackCooldown - deltaTime)
    this.attackDuration = Math.max(0, this.attackDuration - deltaTime)

    let hitbox: AttackHitbox | null = null
    if (
      (this.keys.has('z') || this.keys.has('x')) &&
      this.attackCooldown <= 0
    ) {
      if (!this.keys.has('_attacked')) {
        this.attackCooldown = 0.3
        this.attackDuration = 0.15
        this.keys.add('_attacked')

        hitbox = {
          position: {
            x: this.position.x + (this.facing > 0 ? this.width : -40),
            y: this.position.y + 10,
          },
          width: 40,
          height: 30,
          active: true,
          damage: 10,
        }
      }
    } else if (!this.keys.has('z') && !this.keys.has('x')) {
      this.keys.delete('_attacked')
    }

    // Animation
    this.animTimer += deltaTime
    if (this.animTimer > 0.1) {
      this.animFrame = (this.animFrame + 1) % 4
      this.animTimer = 0
    }

    return hitbox
  }

  render(ctx: CanvasRenderingContext2D) {
    ctx.save()

    // Flip sprite based on facing direction
    if (this.facing < 0) {
      ctx.translate(this.position.x + this.width, this.position.y)
      ctx.scale(-1, 1)
    } else {
      ctx.translate(this.position.x, this.position.y)
    }

    // Body
    ctx.fillStyle = '#4a9eff'
    ctx.fillRect(8, 16, 16, 24)

    // Head
    ctx.fillStyle = '#ffcc99'
    ctx.fillRect(10, 8, 12, 12)

    // Eyes
    ctx.fillStyle = '#000'
    ctx.fillRect(12, 12, 2, 2)
    ctx.fillRect(18, 12, 2, 2)

    // Legs (animated)
    ctx.fillStyle = '#2c5aa0'
    const legOffset =
      Math.abs(this.velocity.x) > 0.1 ? Math.sin(this.animFrame) * 2 : 0
    ctx.fillRect(10, 40, 4, 8 + legOffset)
    ctx.fillRect(18, 40, 4, 8 - legOffset)

    // Attack effect
    if (this.attackDuration > 0) {
      ctx.fillStyle = 'rgba(255, 255, 100, 0.6)'
      ctx.fillRect(this.facing > 0 ? 32 : -40, 10, 40, 30)
    }

    ctx.restore()
  }

  takeDamage(amount: number): boolean {
    this.health = Math.max(0, this.health - amount)
    return this.health <= 0
  }

  reset(x: number, y: number) {
    this.position = { x, y }
    this.velocity = { x: 0, y: 0 }
    this.health = this.maxHealth
    this.jumpCount = 0
    this.attackCooldown = 0
    this.attackDuration = 0
  }
}
